import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import PageHeader from "@/components/PageHeader";
import TButton from "@/components/TButton";
import {
  Play, Loader2, X, ShieldAlert, Activity, Crosshair, Radio,
  FileCheck2, KeyRound, ShieldCheck, BadgeX, FileSignature,
  Clock, Plus, Pause, PlayCircle, Trash2, AlarmClock,
  History, FileCheck, Ban, Zap,
} from "lucide-react";
import { toast } from "sonner";

const SCANNERS = [
  { v: "nuclei", label: "Nuclei" },
  { v: "nmap", label: "Nmap" },
  { v: "openvas", label: "OpenVAS / GVM" },
];

const STATUS_STYLE = {
  queued: "border-slate-700 text-slate-300 bg-slate-900/60",
  running: "border-cyan-500/60 text-cyan-300 bg-cyan-500/10",
  completed: "border-emerald-500/60 text-emerald-300 bg-emerald-500/10",
  failed: "border-rose-500/60 text-rose-300 bg-rose-500/10",
  cancelled: "border-amber-500/60 text-amber-300 bg-amber-500/10",
};

const fmtDate = (s) => (s ? new Date(s).toLocaleString() : "—");

const ScanWorkers = () => {
  const { active, activeId } = useEngagement();
  const [scans, setScans] = useState([]);
  const [agents, setAgents] = useState([]);
  const [auths, setAuths] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [audit, setAudit] = useState({ items: [], total: 0, offset: 0, limit: 20 });
  const [auditBusy, setAuditBusy] = useState(false);
  const [expectedRef, setExpectedRef] = useState("");

  const [scanner, setScanner] = useState("nuclei");
  const [mode, setMode] = useState("external");
  const [agentId, setAgentId] = useState("");
  const [targets, setTargets] = useState("");
  const [authId, setAuthId] = useState("");
  const [busy, setBusy] = useState(false);

  // Authorization gate form
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [refInput, setRefInput] = useState("");
  const [authTargets, setAuthTargets] = useState("");
  const [validDays, setValidDays] = useState(30);
  const [issuing, setIssuing] = useState(false);

  // Schedule form
  const [showSchedForm, setShowSchedForm] = useState(false);
  const [schedName, setSchedName] = useState("");
  const [schedScanner, setSchedScanner] = useState("nuclei");
  const [schedTargets, setSchedTargets] = useState("");
  const [schedCadenceMode, setSchedCadenceMode] = useState("interval"); // interval | cron
  const [schedInterval, setSchedInterval] = useState(60);
  const [schedCron, setSchedCron] = useState("0 */6 * * *");
  const [schedAuthId, setSchedAuthId] = useState("");
  const [schedCreating, setSchedCreating] = useState(false);

  const isDemo = !activeId || activeId === "demo-default";

  const load = useCallback(async () => {
    if (!activeId || isDemo) return;
    try {
      const [s, a, z, sch, log] = await Promise.all([
        api.get(`/engagements/${activeId}/scans`),
        api.get(`/engagements/${activeId}/agents`),
        api.get(`/engagements/${activeId}/authorizations`),
        api.get(`/engagements/${activeId}/schedules`),
        api.get(`/engagements/${activeId}/audit-log?offset=0&limit=20`),
      ]);
      setScans(s.data);
      setAgents(a.data);
      setAuths(z.data.items || []);
      setExpectedRef(z.data.expected_ref || "");
      setSchedules(sch.data);
      setAudit(log.data);
    } catch (e) {
      console.error("[scans] load:", e);
    }
  }, [activeId, isDemo]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isDemo) return;
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [load, isDemo]);

  const activeAuths = useMemo(() => auths.filter((a) => a.active), [auths]);

  const issueAuthorization = async () => {
    const ts = authTargets.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
    if (!refInput.trim()) { toast.error("Scope-Letter reference is required"); return; }
    if (ts.length === 0) { toast.error("List at least one authorized target"); return; }
    setIssuing(true);
    try {
      const { data } = await api.post(`/engagements/${activeId}/authorizations`, {
        scope_letter_ref: refInput.trim(),
        authorized_targets: ts,
        valid_days: Number(validDays) || 30,
      });
      toast.success(`Authorization issued · valid ${data.valid_days}d`);
      setAuthId(data.id);
      setShowAuthForm(false);
      setRefInput("");
      setAuthTargets("");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (typeof d === "object") {
        toast.error(d.detail || JSON.stringify(d));
      } else {
        toast.error(d || "Authorization rejected");
      }
    } finally {
      setIssuing(false);
    }
  };

  const revokeAuthorization = async (id) => {
    try {
      await api.post(`/engagements/${activeId}/authorizations/${id}/revoke`);
      toast.success("Authorization revoked");
      if (authId === id) setAuthId("");
      load();
    } catch {
      toast.error("Revoke failed");
    }
  };

  const launch = async () => {
    if (!authId) {
      toast.error("Select or issue a Scan Authorization first");
      setShowAuthForm(true);
      return;
    }
    const ts = targets.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
    if (ts.length === 0) { toast.error("Enter at least one target"); return; }
    if (mode === "internal" && !agentId) { toast.error("Select a collector agent for internal mode"); return; }
    setBusy(true);
    try {
      await api.post(`/engagements/${activeId}/scans`, {
        scanner, mode, targets: ts, agent_id: mode === "internal" ? agentId : null,
        authorization_id: authId,
      });
      toast.success(`Scan queued: ${scanner.toUpperCase()} → ${ts.length} target(s)`);
      setTargets("");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (typeof d === "object" && d?.unauthorized) {
        toast.error(`Targets not authorized: ${d.unauthorized.join(", ")}`);
      } else if (typeof d === "object" && d?.out_of_scope) {
        toast.error(`Out of scope: ${d.out_of_scope.join(", ")}`);
      } else if (typeof d === "object") {
        toast.error(d.detail || "Scan failed to launch");
      } else {
        toast.error(d || "Scan failed to launch");
      }
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (jobId) => {
    try { await api.post(`/scans/${jobId}/cancel`); load(); }
    catch { toast.error("Cancel failed"); }
  };

  // ---- Schedules ----
  const createSchedule = async () => {
    if (!schedName.trim()) { toast.error("Schedule name required"); return; }
    if (!schedAuthId) { toast.error("Select an authorization"); return; }
    const ts = schedTargets.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
    if (ts.length === 0) { toast.error("Enter at least one target"); return; }
    setSchedCreating(true);
    try {
      const payload = {
        name: schedName.trim(),
        scanner: schedScanner,
        mode: "external",
        targets: ts,
        authorization_id: schedAuthId,
        enabled: true,
      };
      if (schedCadenceMode === "interval") payload.interval_minutes = Number(schedInterval) || 60;
      else payload.cron_expression = schedCron.trim();
      await api.post(`/engagements/${activeId}/schedules`, payload);
      toast.success(`Schedule "${payload.name}" created`);
      setShowSchedForm(false);
      setSchedName(""); setSchedTargets(""); setSchedAuthId("");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (typeof d === "object" && d?.unauthorized) toast.error(`Not authorized: ${d.unauthorized.join(", ")}`);
      else if (typeof d === "object" && d?.out_of_scope) toast.error(`Out of scope: ${d.out_of_scope.join(", ")}`);
      else if (typeof d === "object") toast.error(d.detail || "Schedule creation failed");
      else toast.error(d || "Schedule creation failed");
    } finally {
      setSchedCreating(false);
    }
  };

  const toggleSchedule = async (sched) => {
    try {
      await api.patch(`/schedules/${sched.id}`, { enabled: !sched.enabled });
      toast.success(`Schedule ${sched.enabled ? "paused" : "resumed"}`);
      load();
    } catch {
      toast.error("Toggle failed");
    }
  };

  const deleteSchedule = async (id) => {
    if (!window.confirm("Delete this schedule?")) return;
    try {
      await api.delete(`/schedules/${id}`);
      toast.success("Schedule deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const loadMoreAudit = async () => {
    if (auditBusy) return;
    setAuditBusy(true);
    try {
      const { data } = await api.get(
        `/engagements/${activeId}/audit-log?offset=${audit.items.length}&limit=20`,
      );
      setAudit((a) => ({
        ...data,
        items: [...a.items, ...data.items],
        offset: 0,
      }));
    } catch {
      toast.error("Failed to load more events");
    } finally {
      setAuditBusy(false);
    }
  };

  const scope = useMemo(() => active?.scope || {}, [active]);
  const selectedAuth = useMemo(
    () => auths.find((a) => a.id === authId) || null,
    [auths, authId],
  );

  return (
    <div className="p-6 max-w-7xl">
      <PageHeader
        tag="Live Operations / Scanner"
        title="Scan Workers"
        subtitle="Launch scope-enforced OpenVAS / Nmap / Nuclei scans against client networks — from the cloud or via a Collector Agent on a client jump host."
      />

      {isDemo && (
        <div className="csx-card border-amber-500/40 bg-amber-500/5 p-4 mb-6 flex items-start gap-3">
          <ShieldAlert className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
          <div className="text-xs">
            <div className="text-amber-300 uppercase tracking-widest font-bold">Live scans disabled on Demo Environment</div>
            <div className="text-slate-400 mt-1">Switch to (or create) a real client engagement from the header to enable scanning. The Demo Environment is read-only for safety.</div>
          </div>
        </div>
      )}

      {!isDemo && (
        <>
          {/* ---------------- Authorization Gate ---------------- */}
          <div data-testid="auth-gate-card" className="csx-card border-fuchsia-500/30 bg-fuchsia-500/[0.04] p-5 mb-6">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex items-start gap-3">
                <FileCheck2 className="h-5 w-5 text-fuchsia-300 mt-0.5" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-300">
                    Authorization Gate · Required
                  </div>
                  <div className="text-sm text-slate-200 mt-1">
                    Scans cannot launch without a valid <span className="text-fuchsia-300 font-semibold">Scope-Letter Reference</span> issued against this engagement.
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1 font-mono">
                    Expected reference for this engagement: <span className="text-cyan-300">{expectedRef || "—"}</span>
                  </div>
                </div>
              </div>
              <button
                data-testid="auth-new-toggle"
                onClick={() => setShowAuthForm((v) => !v)}
                className="text-[11px] uppercase tracking-widest px-3 py-1.5 border border-fuchsia-500/60 text-fuchsia-300 hover:bg-fuchsia-500/10"
              >
                {showAuthForm ? "Close" : "+ New Authorization"}
              </button>
            </div>

            {showAuthForm && (
              <div className="border-t border-fuchsia-500/20 pt-4 mt-2 grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div className="lg:col-span-1">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1 flex items-center gap-1">
                    <KeyRound className="h-3 w-3" /> Scope-Letter Ref #
                  </div>
                  <input
                    data-testid="auth-ref-input"
                    value={refInput}
                    onChange={(e) => setRefInput(e.target.value)}
                    placeholder={expectedRef}
                    className="w-full bg-slate-950 border border-slate-800 px-3 py-2 text-sm font-mono outline-none focus:border-fuchsia-500/70"
                  />
                  <div className="text-[10px] text-slate-500 mt-1">
                    Found at the footer of the signed Scope Letter PDF.
                  </div>
                </div>
                <div className="lg:col-span-1">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">
                    Authorized Targets
                  </div>
                  <textarea
                    data-testid="auth-targets"
                    rows={3}
                    value={authTargets}
                    onChange={(e) => setAuthTargets(e.target.value)}
                    placeholder="10.0.0.5, app.client.com"
                    className="w-full bg-slate-950 border border-slate-800 px-3 py-2 text-sm font-mono outline-none focus:border-fuchsia-500/70"
                  />
                </div>
                <div className="lg:col-span-1 flex flex-col">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Valid (days)</div>
                  <input
                    data-testid="auth-valid-days"
                    type="number" min={1} max={365}
                    value={validDays}
                    onChange={(e) => setValidDays(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 px-3 py-2 text-sm outline-none focus:border-fuchsia-500/70"
                  />
                  <TButton
                    data-testid="auth-issue-btn"
                    className="mt-auto"
                    icon={issuing ? Loader2 : FileSignature}
                    onClick={issueAuthorization}
                    disabled={issuing}
                  >
                    {issuing ? "Issuing..." : "Issue Authorization"}
                  </TButton>
                </div>
              </div>
            )}

            {/* Active authorizations list */}
            {auths.length > 0 && (
              <div className="mt-4 space-y-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">
                  Issued Authorizations
                </div>
                {auths.map((a) => {
                  const isSelected = a.id === authId;
                  return (
                    <button
                      key={a.id}
                      data-testid={`auth-row-${a.id}`}
                      onClick={() => a.active && setAuthId(a.id)}
                      className={[
                        "w-full text-left border p-3 flex items-center justify-between gap-3 transition-all",
                        a.active
                          ? (isSelected
                              ? "border-fuchsia-400 bg-fuchsia-500/10"
                              : "border-slate-800 hover:border-fuchsia-500/40")
                          : "border-slate-900 bg-slate-950/40 opacity-60 cursor-not-allowed",
                      ].join(" ")}
                      disabled={!a.active}
                    >
                      <div className="flex items-start gap-3">
                        {a.active ? (
                          <ShieldCheck className="h-4 w-4 text-emerald-400 mt-0.5" />
                        ) : (
                          <BadgeX className="h-4 w-4 text-rose-400 mt-0.5" />
                        )}
                        <div>
                          <div className="text-xs font-mono text-cyan-300">{a.scope_letter_ref}</div>
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            {(a.authorized_targets || []).length} target(s) · issued by {a.issued_by} · expires {fmtDate(a.expires_at)}
                          </div>
                          {a.scope_letter_content_sha256 && (
                            <div className="text-[9px] text-slate-600 font-mono mt-0.5" title="SHA-256 of the canonical scope content at issuance — tamper-evident binding">
                              scope-sha256: {a.scope_letter_content_sha256.slice(0, 16)}…
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={[
                          "text-[10px] uppercase tracking-widest px-1.5 py-0.5 border",
                          a.active
                            ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/10"
                            : "border-rose-500/60 text-rose-300 bg-rose-500/10",
                        ].join(" ")}>
                          {a.active ? "active" : (a.status || "expired")}
                        </span>
                        {a.active && (
                          <span
                            role="button"
                            data-testid={`auth-revoke-${a.id}`}
                            onClick={(e) => { e.stopPropagation(); revokeAuthorization(a.id); }}
                            className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-rose-400"
                          >
                            revoke
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {auths.length === 0 && !showAuthForm && (
              <div className="text-xs text-slate-500 mt-2">
                No authorizations issued yet for this engagement.
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2 csx-card p-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-3 flex items-center gap-2">
                <Crosshair className="h-3 w-3" /> New Scan
              </div>

              <div className="grid grid-cols-3 gap-2 mb-3">
                {SCANNERS.map((s) => (
                  <button key={s.v} data-testid={`scanner-${s.v}`}
                    onClick={() => setScanner(s.v)}
                    className={["text-[11px] uppercase tracking-widest py-2 border transition-all",
                      scanner === s.v ? "border-cyan-400 bg-cyan-500/10 text-cyan-300" : "border-slate-800 text-slate-400 hover:border-slate-700",
                    ].join(" ")}>{s.label}</button>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-2 mb-3">
                {["external", "internal"].map((m) => (
                  <button key={m} data-testid={`mode-${m}`}
                    onClick={() => setMode(m)}
                    className={["text-[11px] uppercase tracking-widest py-2 border transition-all",
                      mode === m ? "border-fuchsia-500 bg-fuchsia-500/10 text-fuchsia-300" : "border-slate-800 text-slate-400 hover:border-slate-700",
                    ].join(" ")}>{m === "external" ? "Cloud (External)" : "Via Agent (Internal)"}</button>
                ))}
              </div>

              {mode === "internal" && (
                <div className="mb-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Collector Agent</div>
                  <select data-testid="agent-select" value={agentId} onChange={(e) => setAgentId(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 px-3 py-2 text-sm">
                    <option value="">— select agent —</option>
                    {agents.map((a) => (
                      <option key={a.id} value={a.id}>{a.name} ({a.status})</option>
                    ))}
                  </select>
                  {agents.length === 0 && (
                    <div className="text-[10px] text-amber-300 mt-1">No agents registered yet — go to Engagements page to register one.</div>
                  )}
                </div>
              )}

              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">
                  Authorization
                </div>
                <select
                  data-testid="auth-select"
                  value={authId}
                  onChange={(e) => setAuthId(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 px-3 py-2 text-sm font-mono"
                >
                  <option value="">— select active authorization —</option>
                  {activeAuths.map((a) => {
                    const label = `${a.scope_letter_ref} · ${(a.authorized_targets || []).length} target(s) · exp ${new Date(a.expires_at).toLocaleDateString()}`;
                    return <option key={a.id} value={a.id}>{label}</option>;
                  })}
                </select>
                {selectedAuth && (
                  <div className="text-[10px] text-slate-500 mt-1 font-mono break-all">
                    Targets covered: {(selectedAuth.authorized_targets || []).join(", ")}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Targets (comma or newline separated)</div>
                <textarea data-testid="targets" value={targets} onChange={(e) => setTargets(e.target.value)}
                  rows={3}
                  className="w-full bg-slate-950 border border-slate-800 px-3 py-2 text-sm font-mono outline-none focus:border-cyan-500/70"
                  placeholder="10.0.0.5, app.client.com, 10.0.0.0/28..." />
              </div>

              <TButton data-testid="launch-scan" className="w-full" icon={busy ? Loader2 : Play} onClick={launch} disabled={busy || !authId}>
                {busy ? "Queueing..." : (authId ? `Launch ${scanner.toUpperCase()} Scan` : "Select Authorization to Launch")}
              </TButton>
            </div>

            <div className="csx-card p-5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-400 mb-3 flex items-center gap-2">
                <ShieldAlert className="h-3 w-3" /> Active Scope
              </div>
              <div className="space-y-2 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Engagement</div>
                  <div className="text-slate-100">{active?.name || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-2">Domains</div>
                  <div className="font-mono text-cyan-300 break-all">{(scope.domains || []).join(", ") || "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-2">IP Ranges</div>
                  <div className="font-mono text-cyan-300 break-all">{(scope.ip_ranges || []).join(", ") || "—"}</div>
                </div>
                {(scope.excluded || []).length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-2">Excluded (kill-list)</div>
                    <div className="font-mono text-rose-300 break-all">{(scope.excluded || []).join(", ")}</div>
                  </div>
                )}
                <div className="pt-3 mt-3 border-t border-slate-800 text-[10px] text-slate-500 leading-relaxed">
                  The scanner refuses any target outside scope, and the authorization gate further restricts to the targets explicitly authorized in the signed Scope Letter.
                </div>
              </div>
            </div>
          </div>

          <div className="csx-card p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-800 text-[10px] uppercase tracking-[0.3em] text-cyan-400 flex items-center gap-2">
              <Activity className="h-3 w-3" /> Scan Queue & History
            </div>
            {scans.length === 0 ? (
              <div className="px-4 py-8 text-center text-slate-500 text-sm">No scans yet for this engagement.</div>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-800 bg-slate-950/40">
                    <th className="py-2 px-4">Scanner</th>
                    <th className="px-4">Mode</th>
                    <th className="px-4">Targets</th>
                    <th className="px-4">Auth Ref</th>
                    <th className="px-4">Status</th>
                    <th className="px-4">Progress</th>
                    <th className="px-4">Findings</th>
                    <th className="px-4">When</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {scans.map((s) => (
                    <tr key={s.id} data-testid={`scan-row-${s.id}`} className="border-b border-slate-900 hover:bg-slate-900/40">
                      <td className="py-2 px-4 font-mono text-cyan-300 uppercase">{s.scanner}</td>
                      <td className="px-4 text-slate-400 uppercase tracking-widest text-[10px]">{s.mode}</td>
                      <td className="px-4 font-mono text-slate-300 truncate max-w-xs">{s.targets.join(", ")}</td>
                      <td className="px-4 font-mono text-fuchsia-300 text-[10px]">{s.scope_letter_ref || "—"}</td>
                      <td className="px-4">
                        <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${STATUS_STYLE[s.status] || STATUS_STYLE.queued}`}>
                          {s.status}
                        </span>
                      </td>
                      <td className="px-4">
                        <div className="w-24 h-1 bg-slate-800">
                          <div className={`h-full ${s.status === "failed" ? "bg-rose-500" : "bg-cyan-500"}`} style={{ width: `${s.progress}%` }} />
                        </div>
                      </td>
                      <td className="px-4 font-mono text-fuchsia-300">{s.findings_count}</td>
                      <td className="px-4 text-slate-500 text-[10px]">{new Date(s.created_at).toLocaleString()}</td>
                      <td className="px-4">
                        {(s.status === "queued" || s.status === "running") && (
                          <button onClick={() => cancel(s.id)} title="Cancel" className="text-slate-500 hover:text-rose-400">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {agents.length > 0 && (
            <div className="csx-card p-5 mt-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-3 flex items-center gap-2">
                <Radio className="h-3 w-3" /> Registered Collector Agents
              </div>
              <ul className="space-y-2 text-xs">
                {agents.map((a) => (
                  <li key={a.id} className="flex items-center justify-between border border-slate-800 p-3">
                    <div>
                      <div className="text-slate-100">{a.name}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{a.id}</div>
                    </div>
                    <div className="text-right">
                      <div className={[
                        "text-[10px] uppercase tracking-widest",
                        a.status === "online" ? "text-emerald-300" : a.status === "offline" ? "text-amber-300" : "text-slate-500"
                      ].join(" ")}>{a.status}</div>
                      <div className="text-[10px] text-slate-500">{a.last_seen ? new Date(a.last_seen).toLocaleTimeString() : "never"}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* ---------------- Continuous Monitoring ---------------- */}
          <div data-testid="schedules-card" className="csx-card p-0 mt-6 overflow-hidden border-cyan-500/30">
            <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between bg-cyan-500/[0.04]">
              <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-300 flex items-center gap-2">
                <AlarmClock className="h-3 w-3" /> Continuous Monitoring · Schedules
              </div>
              <button
                data-testid="sched-new-toggle"
                onClick={() => setShowSchedForm((v) => !v)}
                className="text-[10px] uppercase tracking-widest px-3 py-1 border border-cyan-500/60 text-cyan-300 hover:bg-cyan-500/10 flex items-center gap-1"
              >
                <Plus className="h-3 w-3" /> {showSchedForm ? "Close" : "New Schedule"}
              </button>
            </div>

            {showSchedForm && (
              <div className="p-4 border-b border-slate-800 grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Schedule Name</div>
                  <input data-testid="sched-name" value={schedName} onChange={(e) => setSchedName(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 px-3 py-2 text-sm" placeholder="Weekly perimeter sweep" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Scanner</div>
                  <select data-testid="sched-scanner" value={schedScanner} onChange={(e) => setSchedScanner(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 px-3 py-2 text-sm">
                    {SCANNERS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Authorization</div>
                  <select data-testid="sched-auth" value={schedAuthId} onChange={(e) => setSchedAuthId(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 px-3 py-2 text-sm font-mono">
                    <option value="">— select active auth —</option>
                    {activeAuths.map((a) => {
                      const label = `${a.scope_letter_ref} · ${(a.authorized_targets || []).length} target(s)`;
                      return <option key={a.id} value={a.id}>{label}</option>;
                    })}
                  </select>
                </div>
                <div className="lg:col-span-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Targets</div>
                  <textarea data-testid="sched-targets" value={schedTargets} onChange={(e) => setSchedTargets(e.target.value)} rows={2}
                    className="w-full bg-slate-950 border border-slate-800 px-3 py-2 text-sm font-mono"
                    placeholder="Comma or newline separated — must be in authorized targets list" />
                </div>
                <div className="lg:col-span-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">Cadence</div>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <button data-testid="sched-cadence-interval" onClick={() => setSchedCadenceMode("interval")}
                      className={["text-[11px] uppercase tracking-widest py-2 border",
                        schedCadenceMode === "interval" ? "border-cyan-400 bg-cyan-500/10 text-cyan-300" : "border-slate-800 text-slate-400",
                      ].join(" ")}>Interval</button>
                    <button data-testid="sched-cadence-cron" onClick={() => setSchedCadenceMode("cron")}
                      className={["text-[11px] uppercase tracking-widest py-2 border",
                        schedCadenceMode === "cron" ? "border-fuchsia-500 bg-fuchsia-500/10 text-fuchsia-300" : "border-slate-800 text-slate-400",
                      ].join(" ")}>Cron Expression</button>
                  </div>
                  {schedCadenceMode === "interval" ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400">Every</span>
                      <input data-testid="sched-interval" type="number" min={5} value={schedInterval}
                        onChange={(e) => setSchedInterval(e.target.value)}
                        className="w-24 bg-slate-950 border border-slate-800 px-3 py-2 text-sm" />
                      <span className="text-xs text-slate-400">minutes (min 5)</span>
                    </div>
                  ) : (
                    <div>
                      <input data-testid="sched-cron" value={schedCron} onChange={(e) => setSchedCron(e.target.value)}
                        className="w-full bg-slate-950 border border-slate-800 px-3 py-2 text-sm font-mono"
                        placeholder="0 */6 * * *" />
                      <div className="text-[10px] text-slate-500 mt-1 font-mono">
                        m h dom mon dow · e.g. "0 */6 * * *" = every 6 hours · "0 2 * * 1" = Mondays 2am
                      </div>
                    </div>
                  )}
                </div>
                <div className="lg:col-span-3 flex justify-end">
                  <TButton data-testid="sched-create-btn" icon={schedCreating ? Loader2 : Plus}
                    onClick={createSchedule} disabled={schedCreating}>
                    {schedCreating ? "Creating..." : "Create Schedule"}
                  </TButton>
                </div>
              </div>
            )}

            {schedules.length === 0 ? (
              <div className="px-4 py-6 text-center text-slate-500 text-sm">
                No schedules yet. Create one to enable continuous monitoring against authorized targets.
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-800 bg-slate-950/40">
                    <th className="py-2 px-4">Name</th>
                    <th className="px-4">Scanner</th>
                    <th className="px-4">Cadence</th>
                    <th className="px-4">Targets</th>
                    <th className="px-4">Last Run</th>
                    <th className="px-4">Next Run</th>
                    <th className="px-4">Runs</th>
                    <th className="px-4">State</th>
                    <th className="px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.map((s) => (
                    <tr key={s.id} data-testid={`sched-row-${s.id}`} className="border-b border-slate-900 hover:bg-slate-900/40">
                      <td className="py-2 px-4 text-slate-100">{s.name}</td>
                      <td className="px-4 font-mono text-cyan-300 uppercase">{s.scanner}</td>
                      <td className="px-4 font-mono text-fuchsia-300">
                        {s.cron_expression
                          ? <span title="cron"><Clock className="inline h-3 w-3 mr-1" />{s.cron_expression}</span>
                          : <span>every {s.interval_minutes}m</span>}
                      </td>
                      <td className="px-4 font-mono text-slate-300 truncate max-w-[200px]">{(s.targets || []).join(", ")}</td>
                      <td className="px-4 text-slate-500 text-[10px]">{s.last_run_at ? new Date(s.last_run_at).toLocaleString() : "—"}</td>
                      <td className="px-4 text-slate-300 text-[10px]">{s.next_run_at ? new Date(s.next_run_at).toLocaleString() : "—"}</td>
                      <td className="px-4 font-mono text-fuchsia-300">{s.runs_count || 0}</td>
                      <td className="px-4">
                        <span className={["text-[10px] uppercase tracking-widest px-1.5 py-0.5 border",
                          s.enabled ? "border-emerald-500/60 text-emerald-300 bg-emerald-500/10" : "border-amber-500/60 text-amber-300 bg-amber-500/10",
                        ].join(" ")}>{s.enabled ? "active" : "paused"}</span>
                        {s.last_skip_reason && (
                          <div className="text-[9px] text-rose-300 mt-1 font-mono">{s.last_skip_reason}</div>
                        )}
                      </td>
                      <td className="px-4">
                        <div className="flex items-center gap-2">
                          <button
                            data-testid={`sched-toggle-${s.id}`}
                            onClick={() => toggleSchedule(s)}
                            title={s.enabled ? "Pause" : "Resume"}
                            className="text-slate-500 hover:text-cyan-300"
                          >
                            {s.enabled ? <Pause className="h-3.5 w-3.5" /> : <PlayCircle className="h-3.5 w-3.5" />}
                          </button>
                          <button
                            data-testid={`sched-delete-${s.id}`}
                            onClick={() => deleteSchedule(s.id)}
                            title="Delete schedule"
                            className="text-slate-500 hover:text-rose-400"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ---------------- Audit Trail ---------------- */}
          <div data-testid="audit-trail-card" className="csx-card p-0 mt-6 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-800 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-fuchsia-300">
              <History className="h-3 w-3" /> Audit Trail
              <span className="text-slate-500 normal-case tracking-normal">
                · <span data-testid="audit-shown">{audit.items.length}</span> of <span data-testid="audit-total">{audit.total}</span> events
              </span>
              <span className="ml-auto text-[9px] text-slate-500 normal-case tracking-normal">Immutable · append-only</span>
            </div>
            {audit.items.length === 0 ? (
              <div className="px-4 py-6 text-center text-slate-500 text-sm">No audit events recorded yet.</div>
            ) : (
              <>
                <ul className="divide-y divide-slate-900">
                  {audit.items.map((e) => {
                    let Icon = Activity;
                    let tone = "text-slate-400";
                    if (e.event_type === "authorization.issued") { Icon = FileCheck; tone = "text-emerald-400"; }
                    else if (e.event_type === "authorization.revoked") { Icon = Ban; tone = "text-rose-400"; }
                    else if (e.event_type === "scan.launched") { Icon = Zap; tone = "text-cyan-400"; }
                    return (
                      <li key={e.id} data-testid={`audit-row-${e.id}`} className="px-4 py-2.5 flex items-start gap-3 text-xs hover:bg-slate-900/40">
                        <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${tone}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] uppercase tracking-widest font-mono ${tone}`}>{e.event_type}</span>
                            <span className="text-[10px] text-slate-500 font-mono">{new Date(e.at).toLocaleString()}</span>
                            <span className="text-[10px] text-slate-500">· {e.actor}</span>
                          </div>
                          <div className="text-slate-300 mt-0.5 break-words">{e.summary}</div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                {audit.items.length < audit.total && (
                  <div className="px-4 py-3 border-t border-slate-800 flex justify-center">
                    <button
                      data-testid="audit-load-more"
                      onClick={loadMoreAudit}
                      disabled={auditBusy}
                      className="text-[10px] uppercase tracking-widest px-4 py-1.5 border border-slate-700 hover:border-fuchsia-500/50 text-slate-300 hover:text-fuchsia-300 transition-all disabled:opacity-50"
                    >
                      {auditBusy ? "Loading..." : `Load more · ${audit.total - audit.items.length} remaining`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ScanWorkers;
